<template>
  <div>
    <!-- Header -->
    <div class="d-flex flex-row mb-6 align-center">
      <div>
        <p class="text-2xl mb-0 text-gradient">Models IA</p>
        <p class="text-sm grey--text mb-0">Gestion des catalogues LLM et configurations par défaut</p>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="openModelDialog()">
        <v-icon left>{{ icons.mdiPlus }}</v-icon>
        Nouveau Modèle
      </v-btn>
    </div>

    <v-card shadow="none" class="elevation-1">
      <!-- Onglets dynamiques -->
      <v-tabs v-model="activeTab" background-color="transparent" color="primary" show-arrows>
        <v-tab>Tous</v-tab>
        <v-tab v-for="p in providers" :key="p.id">
          {{ p.id.charAt(0).toUpperCase() + p.id.slice(1) }}
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-tabs-items v-model="activeTab">
        <!-- CONTENU : TOUS -->
        <v-tab-item>
          <v-data-table
            :headers="headers"
            :items="models"
            :loading="loading"
            class="elevation-0"
          >
            <template #[`item.id`]="{ item }">
              <code class="text-caption">{{ item.id }}</code>
            </template>
            <template #[`item.is_active`]="{ item }">
              <v-icon small :color="item.is_active ? 'success' : 'grey'">
                {{ item.is_active ? icons.mdiCheckCircle : icons.mdiCloseCircle }}
              </v-icon>
            </template>
            <template #[`item.supports_image_generation`]="{ item }">
              <v-icon small :color="item.supports_image_generation ? 'success' : 'grey'">
                {{ item.supports_image_generation ? icons.mdiCheckCircle : icons.mdiCloseCircle }}
              </v-icon>
            </template>
            <template #[`item.actions`]="{ item }">
              <div class="d-flex">
                <v-btn icon small @click="openModelDialog(item)" color="primary">
                  <v-icon small>{{ icons.mdiPencil }}</v-icon>
                </v-btn>
                <v-btn icon small @click="duplicateModel(item)" color="secondary">
                  <v-icon small>{{ icons.mdiContentDuplicate }}</v-icon>
                </v-btn>
                <v-btn v-if="showDevActions" icon small @click="confirmDeleteModel(item)" color="error">
                  <v-icon small>{{ icons.mdiTrashCan }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- CONTENU : PAR FOURNISSEUR -->
        <v-tab-item v-for="p in providers" :key="p.id">
          <!-- Caractéristiques du fournisseur (Bandeau) -->
          <v-sheet color="grey lighten-4" class="pa-4 d-flex align-center border-bottom elevation-0">
            <div>
              <span class="text-overline grey--text">Date d'enregistrement</span><br/>
              <span class="text-body-2 font-weight-medium">{{ formatDate(p.created) }}</span>
            </div>
          </v-sheet>

          <v-data-table
            :headers="headers"
            :items="getModelsByProvider(p.id)"
            :loading="loading"
            class="elevation-0"
          >
            <template #[`item.id`]="{ item }">
              <code class="text-caption">{{ item.id }}</code>
            </template>
            <template #[`item.is_active`]="{ item }">
              <v-icon small :color="item.is_active ? 'success' : 'grey'">
                {{ item.is_active ? icons.mdiCheckCircle : icons.mdiCloseCircle }}
              </v-icon>
            </template>
            <template #[`item.supports_image_generation`]="{ item }">
              <v-icon small :color="item.supports_image_generation ? 'success' : 'grey'">
                {{ item.supports_image_generation ? icons.mdiCheckCircle : icons.mdiCloseCircle }}
              </v-icon>
            </template>
            <template #[`item.actions`]="{ item }">
              <div class="d-flex">
                <v-btn icon small @click="openModelDialog(item)" color="primary">
                  <v-icon small>{{ icons.mdiPencil }}</v-icon>
                </v-btn>
                <v-btn icon small @click="duplicateModel(item)" color="secondary">
                  <v-icon small>{{ icons.mdiContentDuplicate }}</v-icon>
                </v-btn>
                <v-btn v-if="showDevActions" icon small @click="confirmDeleteModel(item)" color="error">
                  <v-icon small>{{ icons.mdiTrashCan }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Modal Formulaire (Édition / Création / Duplication) -->
    <v-dialog v-model="dialog" max-width="750px" persistent>
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ isEditing ? (isDuplicating ? 'Dupliquer le modèle' : 'Éditer le modèle') : 'Nouveau modèle' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field 
                v-model="form.id" 
                label="ID Technique (ex: gpt-4o)" 
                outlined dense
                :disabled="isEditing && !isDuplicating"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.display_name" label="Nom d'affichage" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.doc_url" label="URL Documentation" outlined dense placeholder="https://docs.google.com/..." prepend-inner-icon="mdi-link"></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select 
                v-model="form.provider_id" 
                :items="providers" 
                item-text="id" 
                item-value="id" 
                label="Fournisseur" 
                outlined dense
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.context_window" label="Fenêtre de contexte" type="number" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-switch v-model="form.supports_web_search" label="Web Search" dense hide-details></v-switch>
            </v-col>
            <v-col cols="12" md="3">
              <v-switch v-model="form.supports_vision" label="Vision" dense hide-details></v-switch>
            </v-col>
            <v-col cols="12" md="3">
              <v-switch v-model="form.supports_image_generation" label="Image Generation" dense hide-details></v-switch>
            </v-col>
            <v-col cols="12" md="3">
              <v-switch v-model="form.is_active" label="Modèle Actif" dense hide-details></v-switch>
            </v-col>
            <v-col cols="12">
              <v-textarea 
                v-model="form.instructions" 
                label="Mode d'emploi / Résumé des paramètres" 
                outlined 
                rows="4"
                placeholder="Ex: temperature (0-1), max_tokens, etc."
                hint="S'affiche lors de la configuration des assignations pour aider l'utilisateur"
                persistent-hint
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-textarea 
                v-model="jsonString" 
                label="Paramètres par défaut (JSON)" 
                outlined 
                rows="6"
                placeholder='{ "temperature": 0.7 }'
                class="mt-4"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Annuler</v-btn>
          <v-btn color="primary" @click="save" :loading="saving">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from '@vue/composition-api'
import { 
  mdiPlus, mdiPencil, mdiContentDuplicate, 
  mdiCheckCircle, mdiCloseCircle, mdiRobot, mdiTrashCan 
} from '@mdi/js'
import { Konami, KonamiManager } from "@/utils/konami";
import llprService from '@/services/llpr.service'
import store from '@/store'

export default {
  name: 'LlmModelsList',
  setup() {
    const activeTab = ref(0)
    const loading = ref(false)
    const saving = ref(false)
    const models = ref([])
    const providers = ref([])
    
    // Dev Mode (Konami)
    const showDevActions = ref(false)
    let konamiManager = null

    onMounted(() => {
      fetchData()
      
      // Initialize Konami for "dev" (68, 69, 86)
      const devKonami = new Konami([68, 69, 86], () => {
        showDevActions.value = !showDevActions.value
        store.dispatch('app/setSnackBar', { 
          show: true, 
          text: `Mode Dev ${showDevActions.value ? 'activé' : 'désactivé'}`, 
          color: 'info' 
        })
      })
      konamiManager = new KonamiManager(null, [devKonami])
    })

    onUnmounted(() => {
      if (konamiManager) konamiManager.destroy()
    })

    // Form state
    const dialog = ref(false)
    const isEditing = ref(false)
    const isDuplicating = ref(false)
    const jsonString = ref('{}')
    const form = ref({
      id: '', display_name: '', provider_id: '',
      supports_web_search: false, supports_vision: false,
      supports_image_generation: false,
      context_window: null, default_params: {}, is_active: true
    })

    const headers = [
      { text: 'ID Tech', value: 'id' },
      { text: 'Nom Commercial', value: 'display_name' },
      { text: 'Fournisseur', value: 'provider_id' },
      { text: 'Contexte', value: 'context_window' },
      { text: 'Web', value: 'supports_web_search', align: 'center' },
      { text: 'Vision', value: 'supports_vision', align: 'center' },
      { text: 'Image', value: 'supports_image_generation', align: 'center' },
      { text: 'Actif', value: 'is_active', align: 'center' },
      { text: 'Actions', value: 'actions', align: 'end', sortable: false },
    ]

    const fetchData = async () => {
      loading.value = true
      try {
        const [mRes, pRes] = await Promise.all([
          llprService.getModels(),
          llprService.getProviders()
        ])
        models.value = mRes.data
        providers.value = pRes.data
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const getModelsByProvider = (providerId) => models.value.filter(m => m.provider_id === providerId)

    const openModelDialog = (item = null) => {
      isEditing.value = !!item
      isDuplicating.value = false
      if (item) {
        form.value = { ...item }
        jsonString.value = JSON.stringify(item.default_params, null, 2)
      } else {
        form.value = {
          id: '', display_name: '', provider_id: providers.value[0]?.id || '',
          doc_url: '',
          supports_web_search: false, supports_vision: false,
          supports_image_generation: false,
          context_window: null, instructions: '', default_params: {}, is_active: true
        }
        jsonString.value = '{}'
      }
      dialog.value = true
    }

    const duplicateModel = (item) => {
      isEditing.value = true
      isDuplicating.value = true
      form.value = { 
        ...item, 
        id: `${item.id}-copy`,
        display_name: `${item.display_name} (copie)`
      }
      jsonString.value = JSON.stringify(item.default_params, null, 2)
      dialog.value = true
    }

    const save = async () => {
      try {
        // 1. Validation JSON locale
        let params = {}
        try {
          params = JSON.parse(jsonString.value)
        } catch (e) {
          store.dispatch('app/setSnackBar', { show: true, text: 'Erreur JSON : format invalide', color: 'error' })
          return
        }

        saving.value = true
        
        // 2. Préparation du payload (on ne garde que les champs du schéma Base)
        const payload = {
          id: form.value.id,
          display_name: form.value.display_name,
          provider_id: form.value.provider_id,
          doc_url: form.value.doc_url,
          supports_web_search: form.value.supports_web_search,
          supports_vision: form.value.supports_vision,
          supports_image_generation: form.value.supports_image_generation,
          context_window: form.value.context_window,
          instructions: form.value.instructions,
          default_params: params,
          is_active: form.value.is_active
        }
        
        if (isEditing.value && !isDuplicating.value) {
          await llprService.updateModel(form.value.id, payload)
        } else {
          await llprService.createModel(payload)
        }
        
        store.dispatch('app/setSnackBar', { show: true, text: 'Opération réussie', color: 'success' })
        dialog.value = false
        fetchData()
      } catch (e) {
        console.error('Save Error:', e)
        let errorMsg = 'Erreur lors de la sauvegarde'
        
        if (e.response?.data?.detail) {
          const detail = e.response.data.detail
          errorMsg = typeof detail === 'string' ? detail : JSON.stringify(detail)
        }
        
        store.dispatch('app/setSnackBar', { show: true, text: errorMsg, color: 'error' })
      } finally {
        saving.value = false
      }
    }

    const formatDate = (date) => {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric', month: 'long', day: 'numeric'
      })
    }

    const confirmDeleteModel = async (item) => {
      if (confirm(`Voulez-vous vraiment supprimer le modèle ${item.id} ?`)) {
        try {
          await llprService.deleteModel(item.id)
          store.dispatch('app/setSnackBar', { show: true, text: 'Modèle supprimé', color: 'success' })
          fetchData()
        } catch (e) {
          store.dispatch('app/setSnackBar', { 
            show: true, 
            text: e.response?.data?.detail || 'Erreur lors de la suppression (le modèle est peut-être utilisé)', 
            color: 'error' 
          })
        }
      }
    }

    return {
      activeTab, loading, saving, models, providers, headers,
      dialog, isEditing, isDuplicating, form, jsonString,
      showDevActions, confirmDeleteModel,
      openModelDialog, duplicateModel, save, getModelsByProvider, formatDate,
      icons: { mdiPlus, mdiPencil, mdiContentDuplicate, mdiCheckCircle, mdiCloseCircle, mdiRobot, mdiTrashCan }
    }
  }
}
</script>

<style scoped>
.border-bottom { border-bottom: 1px solid #e0e0e0; }
.text-gradient {
  background: linear-gradient(45deg, #1976D2, #42A5F5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
}
</style>
